import { useMemo } from 'react';
import { useFrontendConfig } from '../../context/FrontendConfigContext';
import { useI18n } from '../../context/useI18n';
import { getMastersFromConfig } from '../../utils/frontendConfigMerge';
import { resolveMediaUrl } from '../../utils/mediaUrl';
import { isFitLiveUiEnabled } from '../../utils/fitLiveModule';

/** Maestros raíz — separados por dominio operativo. */
const MASTERS_DEFAULT = [
  {
    id: 'd28d',
    title: 'Gimnasio virtual',
    desc: 'Programas, ciclos, clases, galería y gimnasios.',
    img: 'https://images.unsplash.com/photo-1518611012118-696072aa579a?auto=format&fit=crop&w=800&q=80',
    alt: 'Operación gimnasio virtual',
    section: 'd28d',
  },
  {
    id: 'food-plan',
    title: 'FOOD_PLAN',
    desc: 'Catálogo, recetas y módulo nutricional.',
    img: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?auto=format&fit=crop&w=800&q=80',
    alt: 'Plan de alimentación',
    superAdminOnly: true,
    section: 'food',
  },
  {
    id: 'training',
    title: 'Training — Coaches',
    desc: 'Coaches, rutinas, galería y asignación de atletas.',
    img: 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?auto=format&fit=crop&w=800&q=80',
    alt: 'Entrenadores',
    superAdminOnly: true,
    section: 'training',
  },
  {
    id: 'futbol',
    title: 'academia.app',
    desc: 'Clubs, escuelas, categorías, equipos y operación de academia.',
    img: 'https://images.unsplash.com/photo-1579952363873-27f3bade9f55?auto=format&fit=crop&w=800&q=80',
    alt: 'Fútbol formativo',
    superAdminOnly: true,
    section: 'futbol',
  },
  {
    id: 'commercial-plans',
    title: 'Planes y licencias',
    desc: 'Oferta comercial: planes Food, Entrenadores e invites.',
    img: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?auto=format&fit=crop&w=800&q=80',
    alt: 'Planes y licencias',
    superAdminOnly: true,
    section: 'global',
  },
  {
    id: 'd28d-configurations',
    title: 'Configuraciones gimnasio',
    desc: 'Apariencia, vigencias y ajustes visuales del gimnasio virtual.',
    img: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=800&q=80',
    alt: 'Configuraciones gimnasio',
    d28dAdminOnly: true,
    section: 'd28d',
  },
  {
    id: 'modulevigencias',
    title: 'Vigencias',
    desc: 'Confirmación de pagos, extensiones y licencias.',
    img: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?auto=format&fit=crop&w=800&q=80',
    alt: 'Vigencias y pagos',
    d28dAdminOnly: true,
    section: 'd28d',
  },
  {
    id: 'configurations',
    title: 'Configuraciones globales',
    desc: 'Pagos, apariencia, auditoría y vigencias del ecosistema.',
    img: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=800&q=80',
    alt: 'Configuraciones',
    superAdminOnly: true,
    section: 'global',
  },
  {
    id: 'spiritual-center',
    title: 'Viento Recio',
    desc: 'Centro de formación espiritual, devocionales y contenido bíblico.',
    img: 'https://images.unsplash.com/photo-1507692049794-9c833407c34e?auto=format&fit=crop&w=800&q=80',
    alt: 'Viento Recio',
    superAdminOnly: true,
    section: 'spiritual',
  },
];

const MASTER_SECTIONS = [
  {
    id: 'global',
    title: 'Maestros Plataforma',
    subtitle: 'Planes comerciales y configuraciones transversales del ecosistema.',
  },
  {
    id: 'food',
    title: 'Maestros FOOD',
    subtitle: 'Operación exclusiva del módulo nutricional.',
  },
  {
    id: 'd28d',
    title: 'Maestros gimnasio virtual',
    subtitle: 'Operación de programas, clases y galería.',
  },
  {
    id: 'training',
    title: 'Maestros TRAINING',
    subtitle: 'Operación exclusiva del módulo Entrenadores.',
  },
  {
    id: 'futbol',
    title: 'Maestros academia.app',
    subtitle: 'Clubs / escuelas por deporte (fútbol, voleibol…). Independiente de Food/Training.',
  },
  {
    id: 'spiritual',
    title: 'Maestros VIENTO RECIO',
    subtitle: 'Formación espiritual y contenido bíblico.',
  },
];

/** IDs legacy → redirigir al hub comercial (compatibilidad bookmarks). */
export const LEGACY_MASTER_REDIRECTS = {
  'd28d-plans': 'commercial-plans',
  'training-plans': 'commercial-plans',
  'program-invites': 'commercial-plans',
  'd28d-zoom': 'd28d',
  'd28d-routines': 'd28d',
  'd28d-configurations': 'configurations',
};

export default function MastersHub({ onOpenMaster, hasAnyRole }) {
  const { config } = useFrontendConfig();
  const { t, lang } = useI18n();
  const hideD28dMasters = isFitLiveUiEnabled();
  const masters = useMemo(() => {
    const fromConfig = getMastersFromConfig(config, MASTERS_DEFAULT, lang);
    const allowedIds = new Set(MASTERS_DEFAULT.map((m) => m.id));
    return fromConfig
      .filter((m) => allowedIds.has(m.id))
      .map((m) => {
        const def = MASTERS_DEFAULT.find((d) => d.id === m.id) || m;
        const merged = { ...def, ...m, section: m.section || def.section };
        if (hideD28dMasters) {
          merged.title = String(merged.title || '').replace(/\bD28D\b/gi, '').replace(/\s+/g, ' ').trim() || def.title;
          merged.desc = String(merged.desc || '').replace(/\bD28D\b/gi, '').replace(/\s+/g, ' ').trim() || def.desc;
        }
        return merged;
      });
  }, [config, lang, hideD28dMasters]);

  const open = (rawId) => {
    const id = LEGACY_MASTER_REDIRECTS[rawId] || rawId;
    onOpenMaster(id);
  };

  const visibleMasters = masters.filter((m) => {
    // Ecosistema Fit Live: D28D no aparece en maestros (producto desconectado del hub).
    if (hideD28dMasters && (m.section === 'd28d' || String(m.id || '').startsWith('d28d'))) {
      return false;
    }
    if (m.superAdminOnly && hasAnyRole && !hasAnyRole(['super_admin'])) return false;
    if (m.d28dAdminOnly && hasAnyRole && !hasAnyRole(['admin_d28d', 'super_admin'])) return false;
    if (m.id === 'commercial-plans' && hasAnyRole && !hasAnyRole(['super_admin'])) return false;
    if (m.id === 'configurations' && hasAnyRole && !hasAnyRole([
      'super_admin', 'admin_d28d', 'admin_gym', 'admin_gym_virtual', 'admin_gimnasio',
      'admin_marca', 'admin_sede', 'entrenador', 'admin_training', 'admin_entrenador', 'nutricionista',
    ])) return false;
    if (m.id === 'modulevigencias' && hasAnyRole && !hasAnyRole(['super_admin', 'admin_d28d'])) return false;
    if ((m.id === 'food-plan' || m.id === 'training' || m.id === 'futbol') && hasAnyRole && !hasAnyRole(['super_admin'])) return false;
    return true;
  });

  return (
    <div className="dashboard-main-view surface-readable space-y-10">
      <header className="services-hero">
        <h2 className="services-hero-title">
          {t('masters.title', 'MAESTROS DEL SISTEMA').split(' ')[0]}{' '}
          <span>{t('masters.title', 'MAESTROS DEL SISTEMA').split(' ').slice(1).join(' ') || 'DEL SISTEMA'}</span>
        </h2>
        <p className="services-hero-subtitle">
          {t('masters.subtitle_v3', 'Administración separada por dominio: plataforma, Food, Training, Fútbol y Viento Recio.')}
        </p>
      </header>

      {MASTER_SECTIONS.map((section) => {
        const items = visibleMasters.filter((m) => m.section === section.id);
        if (!items.length) return null;
        return (
          <section key={section.id} className="space-y-4">
            <div>
              <h3 className="text-xl font-bold text-stone-900">{section.title}</h3>
              <p className="text-sm text-stone-600">{section.subtitle}</p>
            </div>
            <div className="services-hero-grid">
              {items.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  className="service-card-hero"
                  onClick={() => open(m.id)}
                  style={{ textAlign: 'left', border: 'none', padding: 0, background: 'transparent', cursor: 'pointer' }}
                  aria-label={t('masters.open_aria', 'Abrir maestro {title}', { title: m.title })}
                >
                  <img src={resolveMediaUrl(m.img)} alt={m.alt} className="service-card-hero-img" />
                  <div className="service-card-hero-content">
                    <h3 className="service-card-hero-title">{m.title}</h3>
                    <p className="service-card-hero-desc">{m.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
